/**
 * Create account (no frame of its own; plan D4). Creo is by invitation, so this
 * door only opens for an address that was invited or is on Mohtawa's approved
 * list. Built from the Join page's parts: name, email, the password rule, the
 * agreement sentence, and Google as an equal second.
 */
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { CircleAlert, CircleUserRound, WifiOff } from "lucide-react";
import { useAuthStore } from "@/stores/authStore";
import { PublicShell } from "@/components/gettingIn/PublicShell";
import {
  Agreement,
  Column,
  GiButton,
  GiField,
  GoogleButton,
  Head,
  Notice,
  OrDivider,
  QuietAction,
  Ways,
} from "@/components/gettingIn/parts";
import { SignInNoticeView } from "@/components/gettingIn/SignInNotice";
import { useSignIn } from "@/components/gettingIn/useSignIn";
import { usePasswordRule } from "@/components/gettingIn/usePasswordRule";
import {
  EMAIL_PATTERN,
  authCalls,
  readAuthFailure,
  rememberDestination,
  safeRedirect,
} from "@/components/gettingIn/authApi";

type Refusal = "not-invited" | "exists" | "offline" | null;

/** What Check your inbox needs to change the address (D8): kept in memory, never stored. */
export type PendingRegistration = { name: string; email: string; password: string };

export default function SignupPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const login = useAuthStore((s) => s.login);
  const destination = safeRedirect(params.get("redirect"));
  const [name, setName] = useState("");
  const [email, setEmail] = useState(params.get("email") ?? "");
  const [emailError, setEmailError] = useState<string | null>(null);
  const [nameError, setNameError] = useState<string | null>(null);
  const password = usePasswordRule();
  const [pending, setPending] = useState(false);
  const [refusal, setRefusal] = useState<Refusal>(null);

  const google = useSignIn({
    onSignedIn: (session) => {
      login(session.token, session.refreshToken ?? "");
      navigate(destination ?? "/dashboard", { replace: true });
    },
  });

  const submit = async () => {
    const nameOk = name.trim().length >= 2;
    const emailOk = EMAIL_PATTERN.test(email.trim());
    setNameError(nameOk ? null : "Add your name.");
    setEmailError(emailOk ? null : "That does not look like an email address.");
    if (!password.check() || !nameOk || !emailOk) return;

    setPending(true);
    setRefusal(null);
    try {
      await authCalls.register(name.trim(), email.trim(), password.value);
      rememberDestination(destination);
      const pendingRegistration: PendingRegistration = {
        name: name.trim(),
        email: email.trim(),
        password: password.value,
      };
      navigate(`/verify?email=${encodeURIComponent(email.trim())}`, { state: { pendingRegistration } });
    } catch (error) {
      const failure = readAuthFailure(error);
      if (failure.kind === "offline") setRefusal("offline");
      else if (failure.kind === "coded" && failure.code === "SIGNUP_NOT_ALLOWED") setRefusal("not-invited");
      else if (failure.kind === "coded" && failure.code === "ACCOUNT_EXISTS") setRefusal("exists");
      else if (failure.kind === "coded" && typeof failure.data.message === "string") setEmailError(failure.data.message);
      else setRefusal("offline");
    } finally {
      setPending(false);
    }
  };

  return (
    <PublicShell still="sitting" title="Create account" topAction={<QuietAction to="/login">Sign in</QuietAction>}>
      <Column>
        <Head title="Create account" sub="Use the address your invitation went to." />
        <SignInNoticeView notice={google.notice} />
        {refusal === "not-invited" ? (
          <Notice tone="info" icon={CircleUserRound}>
            This address has no invitation. Use the address your invitation went to.
          </Notice>
        ) : refusal === "exists" ? (
          <Notice tone="info" icon={CircleAlert}>
            An account already uses this address. Sign in instead, or reset your password.
          </Notice>
        ) : refusal === "offline" ? (
          <Notice tone="attention" icon={WifiOff}>
            Creo cannot be reached. Check your connection; what you typed stays.
          </Notice>
        ) : null}
        <form
          className="flex w-full flex-col gap-6"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <div className="flex w-full flex-col gap-4">
            <GiField
              label="Your name"
              autoComplete="name"
              placeholder="First and last name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              error={nameError}
              readOnly={pending}
            />
            <GiField
              label="Email"
              type="email"
              autoComplete="email"
              placeholder="you@studio.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={emailError}
              readOnly={pending}
            />
            <GiField
              label="Password"
              secret
              autoComplete="new-password"
              placeholder="Choose a password"
              hint={password.hint}
              error={password.error}
              readOnly={pending}
              {...password.field}
            />
          </div>
          <Ways>
            <GiButton type="submit" loading={pending}>
              Create account
            </GiButton>
            <OrDivider />
            <GoogleButton type="button" onClick={google.google} loading={google.pending === "google"} disabled={pending} />
          </Ways>
        </form>
        <div className="w-full">
          <Agreement verb="creating an account" />
        </div>
      </Column>
    </PublicShell>
  );
}
